import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Stack, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { Chips } from 'components/atoms/Chips';
import { getFieldsByAccessoriesType } from 'pages/AncillariesPage/helpers';
import {
  AccessoryInformationContainer,
  AccessoryInformationTitle,
} from 'pages/AncillariesPage/AccessoriesPage/DetailAccessories/styles';
import { ERouteLinks } from 'models/route';
import { EAccessoryLabels } from 'pages/AncillariesPage/AccessoriesPage/DetailAccessories';
import Link from '@mui/material/Link';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import Button from 'components/atoms/Button';
import { mapAncillaryTypeToTranslationKey } from 'utils/mapAncillaryTypeToTranslation';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { IAccessoryDetails, IAncillaryDetailsCustomfield } from 'models/ancillaries';
import { ECustomFieldType } from 'models/customField';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { omitNullishAndEmpty } from 'utils/omitNullishAndEmpty';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { parseDictionary } from 'helpers/dictionary';
import { EMarkingTypes } from 'constants/Markings';
import { ETransactionTypes } from 'constants/TransactionTypes';

interface IAccessoryDetailsProps {
  id: string;
  accessory?: IAccessoryDetails;
  isForTransactionDetails?: boolean;
  initialTransaction?: boolean;
  visibleCustomFieldsData?: boolean;
  disableActions?: boolean;
  customFieldsData?: IAncillaryDetailsCustomfield[];
  marking?: string;
  transactionType?: ETransactionTypes;
}

const AccessoryDetails = ({
  id,
  accessory,
  isForTransactionDetails = false,
  initialTransaction = false,
  visibleCustomFieldsData,
  disableActions,
  customFieldsData,
  marking,
  transactionType,
}: IAccessoryDetailsProps) => {
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();
  const { loadDictionaries, accessoryDetail, getAccessoryDetail, state } = useAncillaries();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const { shortDateFormat } = useGlobalProperty();
  const [accessoryDetailsData, setAccessoryDetailsData] = useState<
    IAccessoryDetails | undefined
  >(undefined);
  const [showMarkings, setShowMarkings] = useState(false);

  useEffect(() => {
    if (accessoryDetailsData) {
      if (accessoryDetailsData.typeUniqueId) {
        const fields = getFieldsByAccessoriesType(accessoryDetailsData.typeUniqueId);
        setAccessFields(fields);
      } else {
        setAccessFields([]);
      }
    }
  }, [accessoryDetailsData]);

  useEffect(() => {
    (async () => {
      if (id && hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES])) {
        await getAccessoryDetail(id, isForTransactionDetails);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!accessory) {
      setAccessoryDetailsData(accessoryDetail);
      return;
    }

    setAccessoryDetailsData({
      ...accessoryDetail,
      ...omitNullishAndEmpty(accessory),
    } as IAccessoryDetails);
  }, [accessoryDetail]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.ArtAncillaryOptions, EDictionaryTypes.State]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const renderCustomFieldData = (customField: IAncillaryDetailsCustomfield) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  // eslint-disable-next-line consistent-return
  const handleCustomFieldsData = () => {
    if (customFieldsData && customFieldsData.length) {
      return customFieldsData;
    }
    if (accessoryDetailsData?.customFields) {
      return accessoryDetailsData.customFields;
    }
  };

  return (
    <div>
      {accessoryDetailsData && (
        <Grid sx={{ gap: 3 }}>
          <DetailsTemplate
            title={accessoryDetailsData?.type}
            pageType={EDetailsPageTypes.view}
            editPermissions={[EPermission.EDIT_ANCILLARY]}
            redirectToView={() =>
              navigate(`${ERouteLinks.ViewAccessories.replace(':id', id)}`)
            }
            disableActions={disableActions}
            actionButtonFullRow={true}
            actionButtonProps={{
              ...{ size: 'small' },
              ...{ sx: { minWidth: '207px !important' } },
              ...{ label: t('legalEntityDetails.ancillary.accordion.details.link') },
              ...{ sizePX: EButtonSizesPX.px24 },
              ...{ variant: EButtonVariants.outlined },
            }}
          >
            <AccessoryInformationContainer container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <AccessoryInformationTitle variant="subtitle1">
                    {t(EAccessoryLabels.accessoryGeneralInformation)}
                  </AccessoryInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.ancillaryType)}
                  </Typography>
                  <Typography variant="body2">
                    {t(mapAncillaryTypeToTranslationKey(accessoryDetailsData?.ancillaryType))}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.accessoryType)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetailsData?.type}</Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                  >
                    {t(EAccessoryLabels.artId)}
                  </Typography>
                  <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                    {accessoryDetailsData?.artId}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Link
                    href={`${ERouteLinks.ViewAncillaries.replace(
                      ':id',
                      accessoryDetailsData.artUniqueId,
                    ).replace(':type', EArtAncillaryTypeName.ACCESSORY.toLowerCase())}`}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      fullWidth
                      label={t('accessoryDetails.showArtInformation.button')}
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                    />
                  </Link>
                </Stack>
              </Grid>
              {showField('manufacturer') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.manufacturer)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('productName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.productName)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.productName)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('calibreUniqueId') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.calibre)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.calibre)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('composition') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.composition)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.composition)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('proofHouse') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.proofHouse)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.proofHouse)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('hostType') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.hostType)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.hostType)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('magnification') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.magnification)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.magnification)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('utility') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.utility)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.utility)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('reticle') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.reticle)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.reticle)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('lightcolour') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.lightcolour)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.lightcolour)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('capacity') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.capacity)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetailsData?.capacity)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </AccessoryInformationContainer>
            <AccessoryInformationContainer
              size={{ xs: 12 }}
              container
              borderHidden={isForTransactionDetails || !showMarkings}
            >
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <AccessoryInformationTitle variant="subtitle1">
                    {t(EAccessoryLabels.accessoryDetailedInformation)}
                  </AccessoryInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.firecycleID)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(accessoryDetailsData?.fireCycleId)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.serialNumber)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(accessoryDetailsData?.serialNumber)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.componentState)}
                  </Typography>
                  <Typography variant="body2">
                    <Chips
                      text={emptyFieldHolder(
                        parseDictionary(state, 'name')[accessoryDetailsData.stateUniqueId],
                      )}
                      variant={
                        renderDataColorByValues(
                          accessoryDetailsData.activityUniqueId,
                          accessoryDetailsData.legalityUniqueId,
                          accessoryDetailsData.stateUniqueId,
                        ).stateField
                      }
                    />
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.exhibitNumber)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(accessoryDetailsData?.exhibitNumber)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.currentOwner)}
                  </Typography>
                  <Typography variant="body2">
                    {!isForTransactionDetails &&
                    accessoryDetailsData.isOwnerHyperLinkActive ? (
                      <StyledLegalLink
                        legalId={accessoryDetailsData.ownerId}
                        legalType={getLegalEntityType(accessoryDetailsData.ownerType)}
                        labelText={accessoryDetailsData?.ownerName}
                        legalVisibilityUniqueId={accessoryDetailsData.ownerVisibilityUniqueId}
                      />
                    ) : (
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetailsData?.ownerName)}
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.currentKeeper)}
                  </Typography>
                  <Typography variant="body2">
                    {!isForTransactionDetails &&
                    accessoryDetailsData.isKeeperHyperLinkActive ? (
                      <StyledLegalLink
                        legalId={accessoryDetailsData.keeperId}
                        legalType={getLegalEntityType(accessoryDetailsData.keeperType)}
                        labelText={accessoryDetailsData?.keeperName}
                        legalVisibilityUniqueId={accessoryDetailsData.keeperVisibilityUniqueId}
                      />
                    ) : (
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetailsData?.keeperName)}
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              </Grid>
              {isForTransactionDetails && initialTransaction && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {transactionType === ETransactionTypes.DECLARED_IMPORTED
                        ? t(EAccessoryLabels.importMarking)
                        : t(EAccessoryLabels.appliedMarking)}
                    </Typography>
                    <Typography variant="body2">{emptyFieldHolder(marking)}</Typography>
                  </Stack>
                </Grid>
              )}
              {visibleCustomFieldsData && (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {handleCustomFieldsData()?.map((customField) => (
                    <Grid size={{ xs: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {customField?.translations?.filter(
                          (translation) =>
                            selectedLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name ||
                          customField?.translations?.filter(
                            (translation) =>
                              defaultLanguage?.uniqueId === translation.languageUniqueId,
                          )?.[0]?.name}
                      </Typography>
                      <Typography variant="body2">
                        {renderCustomFieldData(customField)}
                      </Typography>
                    </Grid>
                  ))}
                </>
              )}
            </AccessoryInformationContainer>
            {!isForTransactionDetails && (
              <AccessoryInformationContainer size={{ xs: 12 }} container borderHidden>
                {accessoryDetailsData.markings && showMarkings && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Stack>
                        <AccessoryInformationTitle variant="subtitle1">
                          {t(EAccessoryLabels.accessoryMarkings)}
                        </AccessoryInformationTitle>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.importMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          accessoryDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.IMPORT,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.appliedMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          accessoryDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.APPLIED,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.deactivationMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          accessoryDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.DEACTIVATION,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.exportMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          accessoryDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.EXPORT,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.surplusSaleMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          accessoryDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.SURPLUS_SALE,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.yearOfTransfer)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetailsData.yearOfTransfer)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.confiscationMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          accessoryDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.CONFISCATION,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.yearOfConfiscation)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetailsData.yearOfConfiscation)}
                      </Typography>
                    </Grid>
                  </>
                )}
                {accessoryDetailsData.markings && (
                  <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'flex-end', mt: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        fullWidth
                        label={
                          showMarkings
                            ? t('ancillaryDetails.hideAncillaryMarkings.button')
                            : t('ancillaryDetails.seeAncillaryMarkings.button')
                        }
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        onClick={() => setShowMarkings(!showMarkings)}
                      />
                    </Grid>
                  </Grid>
                )}
              </AccessoryInformationContainer>
            )}
          </DetailsTemplate>
        </Grid>
      )}
    </div>
  );
};

export default AccessoryDetails;
